/**
 * OAuth callback landing page.
 * WorkOS AuthKit handles the token exchange automatically.
 * This page just waits for authentication to complete, then redirects.
 */
import { useEffect, useState, useRef } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useConvexAuth } from 'convex/react';

const AUTH_TIMEOUT_MS = 15000;

export function AuthCallbackPage() {
  const { isLoading, isAuthenticated } = useConvexAuth();
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');
  const [timedOut, setTimedOut] = useState(false);
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    const timer = setTimeout(() => setTimedOut(true), AUTH_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    console.log('[AuthCallback] WorkOS state:', {
      isLoading,
      isAuthenticated,
      elapsed: ((Date.now() - startTimeRef.current) / 1000).toFixed(1) + 's',
    });
  }, [isLoading, isAuthenticated]);

  if (error) {
    return (
      <Navigate
        to={`/signin?error=${encodeURIComponent(error)}&error_description=${encodeURIComponent(searchParams.get('error_description') || '')}`}
        replace
      />
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/app/dashboard" replace />;
  }

  if (timedOut) {
    return (
      <Navigate
        to="/signin?error=callback_timeout&error_description=Authentication+timed+out.+Please+try+again."
        replace
      />
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-6 px-4 text-center">
      <div className="flex flex-col items-center gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        <div className="space-y-1">
          <p className="text-xl font-bold">Signing you in</p>
          <p className="text-sm text-muted-foreground animate-pulse">
            Completing authentication...
          </p>
        </div>
      </div>
    </div>
  );
}
